import React, { useEffect, useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';

import { postIframeMessageState } from 'atoms/iframe';
import { useSetRecoilState } from 'recoil';
import styled from 'styled-components';

import { AndroidDownloadIcon, IosDownloadIcon } from '@components/common/Icons';
import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';
import { ActionableLink } from '@components/parts/Link';

import { MISO_APP_STORES, PAGE_PATH } from '@utils/constants';

type Props = {
  title: string;
  description?: string;
  backgroundColor?: string;
  color?: string;
  buttonBackgroundColor?: string;
  buttonColor?: string;
  action: {
    name: string;
    href: string;
  };
  induceWhenInvisible?: boolean;
  scrollToId?: string;
};

const Wrapper = styled.div<Props>`
  background-color: ${(props) => props.backgroundColor || '#055cf5'};
  color: ${(props) => props.color || 'white'};
  padding: 110px 0 120px;
  @media (min-width: 992px) {
    padding: 70px 0;
  }

  .induce-cta {
    padding: 0;
    font-size: 20px;
    font-weight: 700;
    align-items: center;
    justify-content: center;
    display: none;
    border-radius: 0;
    &.activated {
      display: none;
      display: var(--display-cta);
      margin: 0 24px 30px 24px;
      border-radius: 4px;
      @media (min-width: 992px) {
        display: none;
      }
    }
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 60px;
    z-index: 999;
  }
  .container {
    justify-content: space-between;
    display: flex;
    flex-direction: column;
    text-align: center;
    .catchphrase {
      flex-direction: column;
      display: flex;
      justify-content: center;
      align-items: center;
      .title {
        font-size: 32px;
        @media (min-width: 992px) {
          font-size: 30px;
        }
        margin-bottom: 16px;
      }
      .description {
        margin-bottom: 32px;
        @media (min-width: 992px) {
          font-size: 16px;
          margin-bottom: 0;
        }
      }
    }
    .action {
      display: flex;
      justify-content: center;
      align-items: center;
      a {
        background-color: ${(props) => props.buttonBackgroundColor || 'white'};
        color: ${(props) => props.buttonColor || '#055cf5'};

        @media (max-width: 992px) {
          padding: 16px 48px;
        }

        font-weight: 700;
        font-size: 19px;
        border-radius: 4px;
      }
    }
    @media (min-width: 992px) {
      text-align: left;
      flex-direction: row;
      .catchphrase {
        align-items: flex-start;
        .title {
          margin-bottom: 0;
        }
        .description {
          margin-top: 8px;
        }
      }
    }
  }
`;

const ConditionalButton = styled.button`
  background-color: white;
  color: rgb(5, 92, 245);
  padding: 16px 48px;
  font-weight: 700;
  font-size: 19px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
`;

const ConditionalFloatButton = styled(ConditionalButton)`
  background: #055cf5;
  color: #ffffff;
  font-weight: 700;
  padding: 10px 24px;
`;

const DownloadButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  @media (max-width: 932px) {
    display: none;
  }
`;

const DownloadButton = styled(ActionableLink)`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
`;

const MobileActionLink = styled(ActionableLink)`
  display: none;
  @media (max-width: 932px) {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${(props) => props.theme.buttonBackgroundColor || 'white'};
    color: ${(props) => props.theme.buttonColor || '#055cf5'};
    padding: 16px 48px;
    @media (min-width: 992px) {
      padding: 12px 80px;
    }
    font-weight: 700;
    font-size: 19px;
    border-radius: 4px;
  }
`;

export const LinkToBooking: React.FC<Props> = (props) => {
  const [isInView, setIsInView] = useState<boolean>(false);
  const [isWithoutCRA, setIsWithoutCRA] = useState<boolean>(false);
  const { title, description, action, induceWhenInvisible = true, scrollToId } = props;
  const setPostIframeMessage = useSetRecoilState(postIframeMessageState);

  let activatedClass = isInView ? '' : 'activated';
  if (!induceWhenInvisible) activatedClass = '';

  const handleChange = (isVisible: boolean) => setIsInView(isVisible);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const getPathName = () => {
    return window.location.pathname.replace(/^\/|\/$/g, '');
  };

  const handleActionClickByWithoutCRA = (button_position: string) => {
    const pathname = getPathName();
    switch (pathname) {
      case PAGE_PATH.INTERNET:
      case PAGE_PATH.INTERNET_ZAPIER:
        setPostIframeMessage({ type: 'request-internet-direct-submit', data: { button_position } });
        break;
      default:
        scrollToTop();
        break;
    }
  };

  useEffect(() => {
    const pathname = getPathName();
    switch (pathname) {
      case PAGE_PATH.INTERNET:
      case PAGE_PATH.INTERNET_ZAPIER:
        setIsWithoutCRA(true);
        break;
      default:
        setIsWithoutCRA(false);
        break;
    }
  }, []);

  const handleEventTrackToActionButton = () => {
    const pathname = getPathName();
    if (pathname === PAGE_PATH.MOVING) {
      // @ts-ignore
      const { fbq } = window;
      if (fbq) fbq('track', 'InitiateCheckout');
    }
  };

  const handleActionClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    handleEventTrackToActionButton();

    if (scrollToId != null) {
      event.preventDefault();
      document.getElementById(scrollToId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Wrapper {...props}>
      {isWithoutCRA ? (
        <ConditionalFloatButton
          onClick={() => handleActionClickByWithoutCRA('float')}
          className={`btn induce-cta reveal-animation ${activatedClass}`}
        >
          {action.name}
        </ConditionalFloatButton>
      ) : (
        <ActionableLink
          href={action.href}
          onClick={(event) => handleActionClick(event)}
          className={`btn induce-cta reveal-animation ${activatedClass}`}
        >
          {action.name}
        </ActionableLink>
      )}
      <VisibilitySensor partialVisibility onChange={handleChange}>
        <Container>
          <ScrollReveal className="catchphrase">
            <h3 className="title">{title}</h3>
            {description ? (
              <p className="description">
                {description.split('\\n').map((p, idx) => (
                  <span key={idx}>
                    {p}
                    <br />
                  </span>
                ))}
              </p>
            ) : null}
          </ScrollReveal>
          {action ? (
            <ScrollReveal className="action">
              {isWithoutCRA ? (
                <ConditionalButton onClick={() => handleActionClickByWithoutCRA('bottom')}>
                  {action.name}
                </ConditionalButton>
              ) : (
                <>
                  <DownloadButtonsContainer>
                    <DownloadButton
                      href={MISO_APP_STORES.IOS}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleActionClick(e)}
                    >
                      <IosDownloadIcon />
                    </DownloadButton>
                    <DownloadButton
                      href={MISO_APP_STORES.ANDROID}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleActionClick(e)}
                    >
                      <AndroidDownloadIcon />
                    </DownloadButton>
                  </DownloadButtonsContainer>
                  <MobileActionLink
                    href={action.href}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleActionClick(e)}
                  >
                    {action.name}
                  </MobileActionLink>
                </>
              )}
            </ScrollReveal>
          ) : null}
        </Container>
      </VisibilitySensor>
    </Wrapper>
  );
};
